import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Snackbar,
  Tooltip
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

import BasicInfoStep from "./steps/BasicInfoStep";
import WorkExperienceStep from "./steps/WorkExperienceStep";
import ServicesStep from "./steps/ServicesStep";
import ExperienceModal from "./components/ExperienceModal";
import ConfirmationModal from "../components/modals/ConfirmationModal";
import check from "../assets/check.svg";
import { useSelector, useDispatch } from "react-redux";
import { clearAuthInfo, setAuthInfo } from "@/redux/auth/auth.slice";
import ToastService from "@/service/toaster.service";
import { isVendorProfileComplete } from "@/helpers/vendor.helpers";
import { isBusinessEmail } from "@/helpers/email.helper";
import vendorService from "@/service/vender.service";
import Loader from "@/common/Loader";

const steps = ["Basic Information", "Work Experience", "Services & Links"];

const ProfileWizard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.auth);
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const [isStepValid, setIsStepValid] = useState([true, true, false]); // Track validity for each step
  const [activeStep, setActiveStep] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({});

  const [latitude, setLatitude] = useState();
  const [longitude, setLongitude] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === 'vendor' && isVendorProfileComplete(user)) {
      navigate("/vendor/dashboard");
    }
  }, [user, navigate]);

  const dispatch = useDispatch();


  const [errorsStep1, setErrorsStep1] = useState({
    vendor_type: "",
    full_name: "",
    business_name: "",
    professional_title: "",
    work_email: "",
    phone_number: "",
    address: "",
  });

  const validateFormStep1 = () => {
    const newErrors = {
      vendor_type: formData.vendor_type ? "" : "Vendor type is required",
      full_name: formData.full_name ? "" : "Full name is required",
      business_name: formData.business_name ? "" : "Business name is required",
      professional_title: formData.professional_title ? "" : "Professional title is required",
      work_email: formData.work_email ? "" : "Work email is required",
      phone_number: formData.phone_number ? "" : "Phone number is required",
      address: formData.address ? "" : "Location is required",
    };

    // Email validation (basic format and generic domain validation)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.work_email && !emailRegex.test(formData.work_email)) {
      newErrors.work_email = "Please enter a valid email address";
    } else if (formData.work_email && !isBusinessEmail(formData.work_email)) {
      newErrors.work_email = "Generic email addresses (e.g., gmail.com, yahoo.com) are not allowed";
    }

    // Phone number validation (check for 10 to 11 digits)
    const phoneRegex = /^\d{10,11}$/;
    if (formData.phone_number && !phoneRegex.test(formData.phone_number)) {
      newErrors.phone_number = "Phone number must be 10 to 11 digits long";
    }

    setErrorsStep1(newErrors); // Update errors state

    // Check if all fields are valid
    return Object.values(newErrors).every((error) => error === "");
  };

  const [errorsStep2, setErrorsStep2] = useState({
    description: "",
  });


  const validateFormStep2 = () => {
    const newErrors = {
      description: formData.description ? "" : "Description  is required",
    };
    setErrorsStep2(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const [errorsStep3, setErrorsStep3] = useState({
    services: "",
    work_email: "",
    professional_title: "",
    linkedin: "",
    certifications_affiliations: "",
    licenses: "",
    certificates: "",

  });

  const [servicesByType, setServicesByType] = useState([]);


  const validateFormStep3 = () => {
    const newErrors = {
      // website: formData.website ? "" : "website is required",
      work_email: formData.work_email ? "" : "work Email is required",
      professional_title: formData.professional_title ? "" : "Professional title is required",
      certifications_affiliations: formData.certifications_affiliations ? "" : "Certifications & Affiliations is required",
      licenses: formData.licenses && formData.licenses.length > 0 ? "" : "Licenses are required",
      certificates: formData.certificates && formData.certificates.length > 0 ? "" : "Certificates are required",
      // website: formData.website ? "" : "website is required",
    };
    setErrorsStep3(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };


  useEffect(() => {
    if (user) { setFormData(user); }
  }, []);

  const handleStepValidity = (stepIndex, isValid) => {
    const updatedValidity = [...isStepValid];
    updatedValidity[stepIndex] = isValid;
    setIsStepValid(updatedValidity);
  };

  // const validateServices = () => {
  //   const servicesMapping = {
  //     'Mortgage Pre-Approval': 'mortgage_pre_approval',
  //     'First-Time Home Buyer Loans': 'first_time_home_buyer_loans',
  //     'Refinancing Services': 'refinancing_services',
  //     'Investment Property Loans': 'investment_property_loans',
  //     'Debt Consolidation': 'debt_consolidation',
  //     'Interest Rate Optimization': 'interest_rate_optimization',
  //     'Loan Application Assistance': 'loan_application_assistance',
  //     'Credit Score Consultation': 'credit_score_consultation',
  //     'Reverse Mortgages': 'reverse_mortgages',
  //     'Home Equity Loans': 'home_equity_loans',
  //     'Custom Loan Solutions': 'custom_loan_solutions',
  //     'Construction Loans': 'construction_loans',
  //     'Government-Backed Loans': 'government_backed_loans',
  //   };
  //   return Object.values(servicesMapping).some((field) => formData[field]);
  // };
  

  const validateServices = () => {
    console.log("DEBUG: Selected services ->", formData);

    return Array.isArray(formData.services) && formData.services.length > 0;
  };
  
  const handleRouteBack = () => {
    if (activeStep == 0) {
      localStorage.clear();
      navigate("/vendor/auth/login");
    } else {
      handleBack();
    }
  };

  const handleNext = () => {

    if (activeStep === 0) {
      const isValid = validateFormStep1();
      if (!isValid) {
        return; // Stop if validation fails
      }
    }
    if (activeStep === 1) {
      const isValid = validateFormStep2();
      if (!isValid) {
        return; // Stop if validation fails
      }
    }

    if (activeStep === 2) {
      if (!validateServices()) {
        ToastService.error("Please select at least one service.");
        return;
      }
      const isValid = validateFormStep3();
      console.log(isValid);
      if (!isValid) {
        return; 
      }
    }


    if (activeStep === steps.length - 1) {
      setShowConfirmModal(true);

    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFinish = async () => {

    setShowConfirmModal(false);
    formData.latitude = latitude;
    formData.longitude = longitude;
    const userId  =  user.id;
    await updateVendor(userId, formData);
    localStorage.setItem("role", localStorage.getItem("temp_onboard_role") || "vendor");
    setShowSuccessModal(true);
    setTimeout(() => {
      navigate('/vendor/dashboard');
    }, 1000);

  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAddExperience = (experience) => {
    setFormData({
      ...formData,
      work_experience: [
        ...(formData?.work_experience || []), {
          id: formData.work_experience ? formData.work_experience.length + 1 : 1,
          ...experience,
        },
      ],
    });
    setModalOpen(false);
  };

  const handleUpdateFormData = (field, value) => {
  
    if (field === 'description' || field === 'business_name' || field === 'professional_title' || field === 'website' || field === 'work_email' || field === 'linkedin') {
      value = value.trimStart();
    }
  
    if (field === 'licenses' || field === 'certificates') {
      const files = Array.from(value);
  
      // Max number of files validation
      if (files.length > MAX_FILES) {
        setErrorsStep3((prevErrors) => ({
          ...prevErrors,
          [field]: `You can upload a maximum of ${MAX_FILES} files.`,
        }));
        return;
      }
  
      // Individual file size validation
      const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024);
      if (oversizedFile) {
        setErrorsStep3((prevErrors) => ({
          ...prevErrors,
          [field]: `Each file must be less than ${MAX_FILE_SIZE_MB} MB.`,
        }));
        return;
      }
  
      // Total combined size validation (10MB limit)
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      const MAX_TOTAL_SIZE_MB = 10;
      if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setErrorsStep3((prevErrors) => ({
          ...prevErrors,
          [field]: `Total size of selected files must not exceed ${MAX_TOTAL_SIZE_MB} MB.`,
        }));
        return;
      }
  
      // Clear file errors if all validations pass
      setErrorsStep3((prevErrors) => ({
        ...prevErrors,
        [field]: '',
      }));
  
      value = files;
    }
  
  
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleLocationSelect = (address) => {
    setFormData({
      ...formData,
      address: address.address,
      post_code: address?.postalCode
    });
    setErrorsStep1(address == '');
    setLatitude(address.latitude || ""); // Update the latitude state
    setLongitude(address.longitude || ""); // Update the longitude state
    validateFormStep1()//
  }

  // Render current step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicInfoStep setServicesByType={setServicesByType} formData={formData} updateFormData={handleUpdateFormData} onAddressSelect={handleLocationSelect} setIsValid={(isValid) => handleStepValidity(0, isValid)} errors={errorsStep1} setErrors={setErrorsStep1} />
        );
      case 1:
        return (
          <WorkExperienceStep formData={formData} updateFormData={handleUpdateFormData} onAddExperience={handleOpenModal} setIsValid={(isValid) => handleStepValidity(1, isValid)} errors={errorsStep2} setErrors={setErrorsStep2} />
        );
      case 2:
        return (
          <ServicesStep servicesByType={servicesByType} formData={formData} updateFormData={handleUpdateFormData} setIsValid={(isValid) => handleStepValidity(2, isValid)} errors={errorsStep3} setErrors={setErrorsStep3} />
        );
      default:
        return "Unknown step";
    }
  };

  // const updateVendor = async (id, data) => {
  //   try {
  //     const payload = new FormData();
  //     // payload.append("is_approved", "true");
  //     payload.append("full_name", data.full_name);
  //     payload.append("vendor_type", data.vendor_type);
  //     payload.append("business_name", data.business_name);
  //     payload.append("professional_title", data.professional_title);
  //     payload.append("phone_number", data.phone_number);
  //     payload.append("description", data.description);

  //     if (data.website) payload.append("website", data.website);
  //     if (data.work_email) payload.append("work_email", data.work_email);
  //     if (data.linkedin) payload.append("linkedin", data.linkedin);
  //     if (data.certifications_affiliations) payload.append("certifications_affiliations", data.certifications_affiliations);

  //     // data.append('profile_picture', formData.profile_picture);
  //     payload.append("longitude", data.longitude);
  //     payload.append("latitude", data.latitude);
  //     payload.append("address", data.address);

  //     // Assuming work_experience is provided in formData as a JSON string
  //     // payload.append("work_experience", JSON.stringify(data.work_experience));
  //     if (data.work_experience && data.work_experience.length > 0) {
  //       payload.append("work_experience", JSON.stringify(data.work_experience));
  //     }

  //     //services
  //     // payload.append("services_provided", data.services_provided);
  //     payload.append("mortgage_pre_approval", data.mortgage_pre_approval ?? false);
  //     payload.append("investment_property_loans", data.investment_property_loans ?? false);
  //     payload.append("home_equity_loans", data.home_equity_loans ?? false);
  //     payload.append("first_time_home_buyer_loans", data.first_time_home_buyer_loans ?? false);
  //     payload.append("debt_consolidation", data.debt_consolidation ?? false);
  //     payload.append("custom_loan_solutions", data.custom_loan_solutions ?? false);
  //     payload.append("refinancing_services", data.refinancing_services ?? false);
  //     payload.append("interest_rate_optimization", data.interest_rate_optimization ?? false);
  //     payload.append("construction_loans", data.construction_loans ?? false);
  //     payload.append("loan_application_assistance", data.loan_application_assistance ?? false);
  //     payload.append("government_backed_loans", data.government_backed_loans ?? false);
  //     payload.append("reverse_mortgages", data.reverse_mortgages ?? false);
  //     payload.append("credit_score_consultation", data.credit_score_consultation ?? false);

  //     // File upload
  //     if (data.licenses && data.licenses.length > 0) {
  //       data.licenses.forEach((license) => {
  //         payload.append('licenses', license);
  //       });
  //     }
      
  //     // For uploading multiple certificates
  //     if (data.certificates && data.certificates.length > 0) {
  //       data.certificates.forEach((certificate) => {
  //         payload.append('certificates', certificate);
  //       });
  //     }

  //     // Call the function to submit profile data
  //     const response = await vendorService.updateVendorProfile(id, payload);
  //     if (response) {
  //       localStorage.setItem("role", "vendor");
  //       dispatch(setAuthInfo({ ...response.data.data, role: "vendor", id: response.data.data._id }));
  //     }
  //     ToastService.success("Profile data submitted successfully!");      

  //     setShowConfirmModal(false);
  //     setShowSuccessModal(true);
  //   } catch (error) {
  //     console.error("Error submitting profile data:", error);
  //     ToastService.error("Error submitting profile data. Please try again.");
  //   }
  // };

  const updateVendor = async (id, data) => {
    try {
      setLoading(true);
      const payload = new FormData();
      // payload.append("is_approved", "true");
      payload.append("full_name", data.full_name);
      payload.append("vendor_type", data.vendor_type);
      payload.append("business_name", data.business_name);
      payload.append("professional_title", data.professional_title);
      payload.append("phone_number", data.phone_number);
      payload.append("description", data.description);

      if (data.website) payload.append("website", data.website);
      if (data.work_email) payload.append("work_email", data.work_email);
      if (data.linkedin) payload.append("linkedin", data.linkedin);
      if (data.certifications_affiliations) payload.append("certifications_affiliations", data.certifications_affiliations);

      // data.append('profile_picture', formData.profile_picture);
      if (typeof data.longitude === "number" && typeof data.latitude === "number") {
        payload.append("longitude", data.longitude);
        payload.append("latitude", data.latitude);
      } else {
        console.warn("⚠️ Longitude or latitude is missing or invalid");
      }
      
      payload.append("address", data.address);

      // Assuming work_experience is provided in formData as a JSON string
      // payload.append("work_experience", JSON.stringify(data.work_experience));
      if (data.work_experience && data.work_experience.length > 0) {
        payload.append("work_experience", JSON.stringify(data.work_experience));
      }

      //services
      if (data.services && data.services.length > 0) {
        const servicesValue = data.services.join(',');
        console.log("✅ Appending services as:", servicesValue);
        payload.append("services", servicesValue);
      } else {
        console.warn("⚠️ No services selected or services array is empty.");
      }

      // File upload
      if (data.licenses && data.licenses.length > 0) {
        data.licenses.forEach((license) => {
          payload.append('licenses', license);
        });
      }
      
      // For uploading multiple certificates
      if (data.certificates && data.certificates.length > 0) {
        data.certificates.forEach((certificate) => {
          payload.append('certificates', certificate);
        });
      }

      // Call the function to submit profile data
      const response = await vendorService.updateVendorProfile(id, payload);
      if (response) {
        setLoading(false);
        localStorage.setItem("role", "vendor");
        dispatch(setAuthInfo({ ...response.data.data, role: "vendor", id: response.data.data._id }));
      }
      setLoading(false);
      ToastService.success("Profile data submitted successfully!");      

      setShowConfirmModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      setLoading(false);
      console.error("Error submitting profile data:", error);
      ToastService.error("Error submitting profile data. Please try again.");
    }
  };
  return (
    <>
    {loading && <Loader />}
    <Box sx={{ p: 2, pt: 10, maxWidth: 1200, mx: "auto" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "white",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          borderBottom: "1px solid #e0e0e0",
        }}
      >

    <Toolbar sx={{ justifyContent: "space-between" }}>
      {activeStep === 0 ? (
        <Tooltip title="Back to login" arrow>
          <IconButton
            onClick={handleRouteBack}
            sx={{
              bgcolor: "#F3F4F6",
              color: "#6366F1",
              "&:hover": { bgcolor: "rgba(244, 162, 55, 0.5)" },
              mb: 0,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <IconButton
          onClick={handleRouteBack}
          sx={{
            bgcolor: "#F3F4F6",
            color: "#6366F1",
            "&:hover": { bgcolor: "rgba(244, 162, 55, 0.5)" },
            mb: 0,
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}
    </Toolbar>
      </AppBar>
      <Card sx={{ mb: 4, overflow: "visible", border: "1px solid #cdccf0", }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              color="primary"
            >
              Update Profile Information -=-
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Keep your profile updated to attract more customers. Add details
              about your services, previous work, and professional links.
            </Typography>
          </Box>

          {!isMobile && (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {getStepContent(activeStep)}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              mt: 4,
            }}
          >

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid[activeStep]} 
              sx={{
                width: isMobile ? "100%" : "auto", // Full width on mobile
                px: 4,
                py: 1,
                borderRadius: 2,
                color: "blue",
                backgroundColor: "rgba(0, 123, 255, 0.1)",
                border: "1px solid blue",
                "&:hover": {
                  color: "black",
                  backgroundColor: "rgba(244, 162, 55, 0.5)",
                  border: "1px solid #f59e0b",
                },
                // Add this to handle the border color when the button is disabled
                "&.Mui-disabled": {
                  border: "1px solid gray", // Grey border when disabled
                  backgroundColor: "rgba(169, 169, 169, 0.2)", // Grey background for disabled state
                },
              }}
              endIcon={activeStep === steps.length - 1 ? null : (<Box component="span" sx={{ ml: 1 }}>→</Box>)}
            >
              {activeStep === steps.length - 1 ? "Finish" : "Next"}
            </Button>

          </Box>
        </CardContent>
      </Card>

      <ExperienceModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleAddExperience}
      />

      <ConfirmationModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm & Save Changes"
        message="You have updated your profile information. Do you want to save these changes?"
        onConfirm={handleFinish}
      />

      <ConfirmationModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Changes Saved Successfully!"
        message="Your profile details have been updated successfully!"
        showSuccess
        image={check}
        onConfirm={() => { }}
      />
    </Box>
    </>
  );
};

export default ProfileWizard;
